/**
 * UI-only checks for resolved template variable values.
 *
 * Script generation treats whitespace-only ATS placeholders (e.g. " ") as present.
 * The editor UI should still show "No value found" / "Value not found" for those.
 */
import {
  ResolutionStage,
  type ResolutionStage as ResolutionStageValue,
} from 'api/generated/types';
import { lookupOwn } from 'utils/typeGuards';

const RESOLUTION_STAGE_RANK: Record<ResolutionStageValue, number> = {
  [ResolutionStage.config]: 0,
  [ResolutionStage.send]: 1,
  [ResolutionStage.call]: 2,
};

export const isResolutionStageAvailable = (
  variableStage: ResolutionStageValue,
  maxResolutionStage: ResolutionStageValue
): boolean =>
  RESOLUTION_STAGE_RANK[variableStage] <=
  RESOLUTION_STAGE_RANK[maxResolutionStage];

export const hasDisplayableVariableValue = (
  value: string | null | undefined
): value is string =>
  value !== null && value !== undefined && value.trim() !== '';

export const hasJobVariableValues = (
  values: Record<string, string> | null | undefined
): values is Record<string, string> =>
  values !== null && values !== undefined && Object.keys(values).length > 0;

export const normalizeJobVariableValues = (
  values: Record<string, string> | null | undefined
): Record<string, string> | null =>
  hasJobVariableValues(values) ? values : null;

export type DeprecatedVariables =
  | ReadonlyMap<string, string>
  | ReadonlySet<string>
  | Readonly<Record<string, string>>;

// `instanceof Map` narrows a `ReadonlyMap<string, string>` to `Map<any, any>`, and
// `instanceof` cannot narrow the Readonly* interfaces out of the union, so these
// predicates keep the declared element types.
export const isDeprecatedVariablesMap = (
  deprecatedVariables: DeprecatedVariables
): deprecatedVariables is ReadonlyMap<string, string> =>
  deprecatedVariables instanceof Map;

export const isPlainDeprecatedRecord = (
  deprecatedVariables: DeprecatedVariables
): deprecatedVariables is Readonly<Record<string, string>> =>
  !(deprecatedVariables instanceof Map) &&
  !(deprecatedVariables instanceof Set);

/**
 * The canonical replacement registered for a deprecated variable name, or
 * undefined when the name is not deprecated or its catalog (a Set) names no
 * replacement.
 */
export const lookupDeprecatedReplacement = (
  name: string,
  deprecatedVariables: DeprecatedVariables | undefined
): string | undefined => {
  if (deprecatedVariables === undefined) {
    return undefined;
  }
  if (isDeprecatedVariablesMap(deprecatedVariables)) {
    return deprecatedVariables.get(name);
  }
  return isPlainDeprecatedRecord(deprecatedVariables)
    ? lookupOwn(deprecatedVariables, name)
    : undefined;
};

export const isDeprecatedVariableName = (
  name: string,
  deprecatedVariables: DeprecatedVariables | undefined
): boolean =>
  deprecatedVariables !== undefined &&
  (deprecatedVariables instanceof Set || deprecatedVariables instanceof Map
    ? deprecatedVariables.has(name)
    : Object.prototype.hasOwnProperty.call(deprecatedVariables, name));
